import { Component, Inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { forkJoin, Observable } from 'rxjs';
import { MessageInput, SmsRepeat, SmsType, WhatsappSettings, WhatsappWindowStatesGQL } from 'src/app/core/graphql/generated/gen-types';
import { ResponsiveService } from 'src/app/core/services/admin/responsive.service';
import { BookingService } from 'src/app/core/services/booking/booking.service';
import { CrewService } from 'src/app/core/services/crew/crew.service';
import { FlightService } from 'src/app/core/services/flight/flight.service';
import { SettingsService } from 'src/app/core/services/settings/settings.service';
import { SmsService } from 'src/app/core/services/sms/sms.service';
import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';
import { TranslateCustomService } from 'src/app/core/utility/translate-custom.service';
import { ImageItem } from 'src/app/shared/images-manager/images-manager.component';

@Component({
	selector: 'app-dialog-wa',
	templateUrl: './dialog-wa.component.html',
	styleUrls: ['./dialog-wa.component.scss'],
})
export class DialogWaComponent {
	waDetailForm: UntypedFormGroup;
	smsMessage = { to: null, smsNL: '', smsFR: '', smsEN: '', smsDE: '', role: '' };
	smsTemplate: any;
	smsFooter: any;
	smsUp = false;
	upEnabled = false;
	smsReminder = false;
	smsCancel = false;
	sendTo: string;
	settings: any;
	// 037 FR-029: all non-removed variant docs of this message type — picker shown when > 1
	templateVariants: any[] = [];
	// 037 FR-006b/FR-007: rendered wording snapshot (per language) to detect operator edits, and
	// whether this tenant sends over the official Cloud API (drives the out-of-window warning)
	private renderedSnapshot: any = {};
	cloudProvider = false;
	// 037 FR-006c: how many recipients sit outside the 24h window — they receive the approved
	// template wording (decision 2026-08-31), so edits must trigger a visible warning
	windowChecked = false;
	recipientCount = 0;
	outOfWindowCount = 0;
	chipList: string[] = [];
	chipLists = { nl: [], fr: [], en: [], de: [] };
	languages = {
		nl: 0,
		fr: 0,
		en: 0,
		de: 0,
	};
	langSelected = 'nl';
	dutch: 'nl' | 'du' = 'nl';
	chipListSelected;
	exampleUp = 'https://up.quickrelease.aero/?l=U2FsdG**************';
	LANG = ['nl', 'en', 'fr', 'de'];
	positiveReply;
	negativeReply;
	footerEnabled: boolean = true;
	isMobile = true;
	whatsapp: WhatsappSettings;
	whatsappConnected: boolean = false;
	locationId?: string;
	selectedImages: ImageItem[] = [];
	// 037 US7 (2026-09-03): conversation mode — opened from the booking/crew chat with ONE
	// recipient and a SELECTABLE type (like the email dialog's template toggle), instead of a
	// fixed data.smsTemplate over a full flight. Everything else (language columns, window
	// banners, variant picker, edit warning) is the same dialog the reminder send uses.
	conversationMode = false;
	conversationTypes: { type: string; labelKey: string; flightBound?: boolean }[] = [];
	selectedType: string | null = null;
	// 2026-09-12 (Wim): a booking chat WITHOUT a planned flight hides the flight-bound types
	// (they would only render '-' for every flight variable) — this flag drives the hint line
	flightTypesHidden = false;
	sending = false;

	// 222: cancelFlight mode — "Annuleren" on the flight opens THIS dialog: pick the cancel message
	// (its reason code becomes the cancel reason), then "cancel and send" (sends awaited, then the
	// caller cancels) or "cancel only" (inline warning). data.cancelFlight =
	// { reasons: string[], alreadySent: boolean, cancelMessageReason?: string }
	cancelFlightMode = false;
	cancelReason: string | null = null;
	reasonEditing = false;
	cancelOnlyOpen = false;
	sendError: string | null = null;
	/** while set, sendSMS collects its send observables here instead of firing them */
	private sendCollector: Observable<any>[] | null = null;

	// which canned types belong on which conversation screen (Wim, 2026-09-03); the passenger
	// invitation carries the booking-confirmation flow, locationContactSms belongs to neither.
	// flightBound (2026-09-12, Wim): the type's wording is about ONE planned flight ({{ date }},
	// {{ hour }}, {{ city }}, …) — offered in a booking chat only when the booking has a flight.
	// Crew chats never carry a flight context (sms-by-contact passes flight: null), so their
	// types are deliberately NOT flagged: flagging them would empty the crew list altogether.
	private static readonly CONVERSATION_TEMPLATE_TYPES: Record<string, { type: string; labelKey: string; flightBound?: boolean }[]> = {
		BOO: [
			// 2026-09-04: reservation confirmation — booking-only variables (the e-mail's booking_* set,
			// 2026-09-12), may carry the UP url and upcoming flight options as separate messages (the
			// only type with those toggles)
			{ type: 'bookingConfirmationSms', labelKey: 'COMMUNICATIONS.booking_confirmation_sms' },
			{ type: 'passengerInvitationSms', labelKey: 'COMMUNICATIONS.passenger_invitation_sms', flightBound: true },
			{ type: 'reminderSms', labelKey: 'COMMUNICATIONS.reminder_sms', flightBound: true },
			{ type: 'checkinNotificationSms', labelKey: 'COMMUNICATIONS.checkin_notification_sms', flightBound: true },
			{ type: 'cancelSms', labelKey: 'COMMUNICATIONS.cancel_sms', flightBound: true },
		],
		CRE: [
			{ type: 'crewInvitationSms', labelKey: 'COMMUNICATIONS.crew_invitation_sms' },
			{ type: 'pilotInvitationSms', labelKey: 'COMMUNICATIONS.pilot_invitation_sms' },
			{ type: 'reminderSms', labelKey: 'COMMUNICATIONS.reminder_sms' },
			{ type: 'cancelSms', labelKey: 'COMMUNICATIONS.cancel_sms' },
		],
	};

	// PAX recipients for the reminder/cancel sends. For a cancelled flight the live booking
	// list is empty, so the recorded former bookings are passed in via data.formerBookings
	// and used as-is, without the CONF status filter (070, issue #61).
	// On a finished flight passengers are COMP, not CONF — include them so operators can
	// still message them afterwards, e.g. to share photos (#169)
	get reminderCancelRecipients(): any[] {
		if (this.smsCancel && this.data.formerBookings?.length) return this.data.formerBookings;
		const list = this.data.flight?.bookings?.list ?? [];
		if (this.data.flight?.status === 'FIN') return list.filter((b) => b.status === 'CONF' || b.status === 'COMP');
		return list.filter((b) => b.status === 'CONF');
	}

	constructor(
		public dialogRef: MatDialogRef<DialogWaComponent>,
		public translate: TranslateService,
		public translateCustom: TranslateCustomService,
		public flightService: FlightService,
		public bookingService: BookingService,
		public crewService: CrewService,
		public settingsService: SettingsService,
		private tenantSettingService: TenantSettingService,
		private smsService: SmsService,
		private formBuilder: UntypedFormBuilder,
		private responsiveService: ResponsiveService,
		private whatsappWindowStatesGQL: WhatsappWindowStatesGQL,
		@Inject(MAT_DIALOG_DATA) public data: any,
	) {
		this.conversationMode = !!this.data?.conversation;
		this.cancelFlightMode = !!this.data?.cancelFlight;
		if (this.cancelFlightMode) this.cancelReason = this.data.cancelFlight.cancelMessageReason ?? null;

		// The WhatsApp composer only supports nl/fr/en/de columns. A contact language outside
		// that set (currently 'es', added as a contact language in #179) would land in
		// this.languages[lang] as NaN and break counting/sending. Until the composer gets a
		// first-class 'es' column (#181), clamp any unsupported language to 'en' on the local
		// flight copy so es contacts are handled exactly like the automated-send en fallback.
		this.normalizeUnsupportedLanguages();
		if (this.conversationMode && !['nl', 'fr', 'en', 'de'].includes(this.data.conversation.language)) {
			this.data.conversation = { ...this.data.conversation, language: 'en' };
		}

		if (this.translate.currentLang === 'du') this.dutch = 'du';

		this.responsiveService.viewMobileChanged.subscribe((mobileView) => {
			this.isMobile = mobileView;
		});

		this.waDetailForm = this.formBuilder.group({
			to: '',
			smsNL: '',
			smsFR: '',
			smsEN: '',
			smsDE: '',
			role: '',
			up: false,
			flightOptions: false,
		});

		// check up active or not // check footerEnabled
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((result) => {
			if (result.data.tenantSetting.upEnabled) {
				this.upEnabled = true;
			}
			this.footerEnabled = result.data.tenantSetting.operatorSettings?.sms?.footerEnabled;
			this.cloudProvider = String(result.data.tenantSetting.whatsapp?.provider || '').toUpperCase() === 'CLOUD';
			if (this.cloudProvider && !this.windowChecked) this.checkRecipientWindows();
		});

		this.smsService.getWaConnected()?.subscribe((connected) => {
			this.whatsappConnected = connected;
		});

		if (this.conversationMode) {
			// type is chosen IN the dialog (email-dialog pattern) — preselect the first one.
			// 2026-09-12 (Wim): no planned flight on the booking ⇒ the flight-bound types are left
			// out (their preview would be nothing but '-' placeholders); a hint says why
			const allTypes = DialogWaComponent.CONVERSATION_TEMPLATE_TYPES[this.data.conversation.type] || [];
			const hasFlight = !!this.data.flight?.id;
			this.conversationTypes = hasFlight ? allTypes : allTypes.filter((t) => !t.flightBound);
			this.flightTypesHidden = this.conversationTypes.length < allTypes.length;
			if (this.conversationTypes.length > 0) this.selectType(this.conversationTypes[0].type);
			// the chat hands us the booking query's PARTIAL flight (no location/balloon fields) —
			// fetch the full flight so the preview shows what the send path will really deliver
			if (this.data.flight?.id) {
				this.flightService.get(this.data.flight.id).valueChanges.subscribe((result) => {
					const flight = result.data?.flight;
					if (!flight) return;
					this.data.flight = flight;
					this.normalizeUnsupportedLanguages();
					this.rebuildChips();
					if (this.settings) this.updateTemplate();
				});
			}
		} else {
			this.loadTypeTemplates(this.data.smsTemplate);
		}

		this.rebuildChips();
		if (this.data.whatsapp) {
			this.whatsapp = this.data.whatsapp;
		}

		this.smsMessage.to = this.conversationMode ? this.data.conversation.name || '' : this.data.to + ' (' + this.data.role + ')';
		this.smsMessage.smsNL = this.smsTemplate ? this.smsTemplate['nl'] : '';
		this.smsMessage.smsFR = this.smsTemplate ? this.smsTemplate['fr'] : '';
		this.smsMessage.smsEN = this.smsTemplate ? this.smsTemplate['en'] : '';
		this.smsMessage.smsDE = this.smsTemplate ? this.smsTemplate['de'] : '';
		this.smsMessage.role = this.data.role;
		this.updateForm();
	}

	// Chips are rebuilt whenever the flight context (possibly a partial projection from the
	// booking query, conversation mode) is replaced. Falsy values are skipped — pushing undefined
	// crashed formatChip on every change-detection pass (found via Playwright, 2026-09-03).
	private rebuildChips() {
		this.chipList = [];
		this.chipLists = { nl: [], fr: [], en: [], de: [] };
		const push = (value: string | null | undefined) => {
			if (!value) return;
			this.chipList.push(value);
			this.LANG.forEach((lang) => this.chipLists[lang].push(value));
		};
		const location = this.data.flight?.location;
		if (location) {
			this.locationId = location.id;
			push(location.city);
			push(location.address);
			push(location.description);
		}
		if (this.data.flight?.date) push(this.formatDate(this.data.flight.date, 'nl-BE'));
		if (this.data.flight?.hour) push(this.formatTime(this.data.flight.hour));
		this.chipListSelected = this.chipLists[this.langSelected] ?? [];
	}

	// Any contact/crew/location language the composer has no column for (anything other than
	// nl/fr/en/de — currently 'es', added as a contact language in #179) is remapped to 'en',
	// matching the automated-send en fallback. This is done on SHALLOW COPIES reassigned onto
	// the dialog's own data payload — the shared flight object (Apollo cache / flight-detail
	// view) is never mutated. Covers both the live booking list and the formerBookings list
	// used for cancelled-flight cancellation sends. Removed once #181 adds a real 'es' column.
	private normalizeUnsupportedLanguages() {
		const supported = ['nl', 'fr', 'en', 'de'];
		const needsClamp = (lang: string) => lang != null && !supported.includes(lang);
		const clampBooking = (b: any) => (b && needsClamp(b.contactLanguage) ? { ...b, contactLanguage: 'en' } : b);

		const flight = this.data?.flight;
		if (flight) {
			this.data.flight = {
				...flight,
				pilot:
					flight.pilot?.crew && needsClamp(flight.pilot.crew.language)
						? { ...flight.pilot, crew: { ...flight.pilot.crew, language: 'en' } }
						: flight.pilot,
				groundCrews: flight.groundCrews
					? flight.groundCrews.map((c: any) =>
							c?.crew && needsClamp(c.crew.language) ? { ...c, crew: { ...c.crew, language: 'en' } } : c
						)
					: flight.groundCrews,
				bookings: flight.bookings?.list ? { ...flight.bookings, list: flight.bookings.list.map(clampBooking) } : flight.bookings,
				location:
					flight.location?.contact && needsClamp(flight.location.contact.language)
						? { ...flight.location, contact: { ...flight.location.contact, language: 'en' } }
						: flight.location,
			};
		}
		if (this.data?.formerBookings?.length) {
			this.data.formerBookings = this.data.formerBookings.map(clampBooking);
		}
	}

	get imageSupported() {
		return this.smsReminder;
	}

	// UP-url / flight-options toggles: batch mode keeps today's rule; conversation mode shows them
	// only for the reservation confirmation (2026-09-04 — "komende vaarten meesturen + up url")
	get showUpToggle(): boolean {
		if (this.conversationMode) {
			return this.upEnabled && this.data.conversation.type === 'BOO' && this.selectedType === SmsType.BookingConfirmationSms;
		}
		return this.upEnabled && this.data.smsTemplate != 'locationContactSms';
	}

	// 2026-09-08 (Wim, FR-006e) / revised 2026-09-12 (FR-006e v3): the UP url and the upcoming-flight
	// options are SEPARATE messages. Outside the 24h window the backend sends them as fixed
	// PLATFORM templates (wording set by Quick Release, not editable by the operator) — so the
	// toggles stay available; this note tells the operator that out-of-window recipients get the
	// fixed wording. No SMS hybrid: SMS remains the emergency fallback for the whole message only.
	get auxAsTemplate(): boolean {
		return this.cloudProvider && this.windowChecked && this.outOfWindowCount > 0;
	}

	get showFlightOptionsToggle(): boolean {
		if (this.conversationMode) {
			return this.data.conversation.type === 'BOO' && this.selectedType === SmsType.BookingConfirmationSms;
		}
		return this.data.smsTemplate != 'locationContactSms';
	}

	get images() {
		return this.selectedImages.map((image) => image.url);
	}

	formatChip(chip: string) {
		if (!chip) return '';
		if (chip.length > 16) {
			return chip.substr(0, 16) + '...';
		} else {
			return chip;
		}
	}

	// 037 US7 (2026-09-03): type chosen inside the dialog (conversation mode) — email-dialog pattern
	selectType(type: string) {
		this.selectedType = type;
		this.templateVariants = [];
		this.settings = null;
		// a hidden-but-still-true toggle from a previous type must not ride along
		if (type !== SmsType.BookingConfirmationSms) this.waDetailForm.patchValue({ up: false, flightOptions: false });
		this.loadTypeTemplates(type);
	}

	private loadTypeTemplates(type: string) {
		this.smsService.getSmsTemplates(type).valueChanges.subscribe((result) => {
			if ((this.conversationMode ? this.selectedType : this.data.smsTemplate) !== type) return; // stale response
			if (result.data.getSmsTemplates.length > 0) {
				// 037 FR-029: a type may have several variants; the picker swaps the wording.
				// Default to the first variant that actually carries text — an empty variant as
				// default would show an empty composer. Conversation mode (2026-09-03): prefer a
				// variant with text in the RECIPIENT's language — the single column shown there —
				// otherwise the default could land on a variant whose column is empty.
				this.templateVariants = result.data.getSmsTemplates;
				const conversationLang = this.conversationMode ? this.data.conversation.language : null;
				this.settings =
					(conversationLang ? this.templateVariants.find((variant) => variant.template?.[conversationLang]) : null) ||
					// 222: cancel messages — the variant linked to the flight's reason, else the default
					(type === SmsType.CancelSms ? this.cancelVariantFor(this.data?.preselectReason ?? null) : null) ||
					this.templateVariants.find((variant) => Object.values(variant.template || {}).some((wording) => wording)) ||
					this.templateVariants[0];
				if (this.cancelFlightMode && !this.data.cancelFlight.alreadySent && this.settings?.reasonCode) this.cancelReason = this.settings.reasonCode;
				this.updateTemplate();
			}
		});
	}

	// 222: the cancel variant for a reason (a reason belongs to at most one variant); no match ⇒ the
	// default variant: first (creation order) without a reason code that carries text
	private cancelVariantFor(reason: string | null) {
		const hasText = (variant) => Object.values(variant.template || {}).some((wording) => wording);
		return (
			(reason ? this.templateVariants.find((variant) => variant.reasonCode === reason) : null) ||
			this.templateVariants.find((variant) => !variant.reasonCode && hasText(variant)) ||
			null
		);
	}

	// conversation mode: single recipient, no batch language counting; invitation types keep
	// their yes/no footer (the reply flow is the same whether sent from flight or chat)
	private applyConversationTypeSetup(smsType: string) {
		this.smsFooter = '';
		this.positiveReply = undefined;
		this.negativeReply = undefined;
		const footerOf = (key: string, positiveKey: string, negativeKey: string) => {
			this.smsFooter = {
				nl: this.translateCustom.translate(key, this.dutch),
				fr: this.translateCustom.translate(key, 'fr'),
				en: this.translateCustom.translate(key, 'en'),
				de: this.translateCustom.translate(key, 'de'),
			};
			this.positiveReply = {
				nl: this.translateCustom.translate(positiveKey, this.dutch),
				fr: this.translateCustom.translate(positiveKey, 'fr'),
				en: this.translateCustom.translate(positiveKey, 'en'),
				de: this.translateCustom.translate(positiveKey, 'de'),
			};
			this.negativeReply = {
				nl: this.translateCustom.translate(negativeKey, this.dutch),
				fr: this.translateCustom.translate(negativeKey, 'fr'),
				en: this.translateCustom.translate(negativeKey, 'en'),
				de: this.translateCustom.translate(negativeKey, 'de'),
			};
		};
		if (smsType === SmsType.PassengerInvitationSms) footerOf('BOOKINGS.answer_flight_yes_no', 'BOOKINGS.flight_yes', 'BOOKINGS.flight_no');
		if (smsType === SmsType.PilotInvitationSms) footerOf('FLIGHTS.answer_pilot_yes_no', 'FLIGHTS.pilot_yes', 'FLIGHTS.pilot_no');
		if (smsType === SmsType.CrewInvitationSms) footerOf('FLIGHTS.answer_crew_yes_no', 'BOOKINGS.crew_yes', 'BOOKINGS.crew_no');
		this.sendTo = 'send';
		this.smsMessage.role = 'conversation';
		this.languages = { nl: 0, fr: 0, en: 0, de: 0 };
		this.languages[this.data.conversation.language] = 1;
	}

	updateTemplate() {
		this.smsTemplate = {
			nl: this.parseTemplate(this.settings.template.nl, this.data.flight, 'nl'),
			fr: this.parseTemplate(this.settings.template.fr, this.data.flight, 'fr'),
			en: this.parseTemplate(this.settings.template.en, this.data.flight, 'en'),
			de: this.parseTemplate(this.settings.template.de, this.data.flight, 'de'),
		};
		// 037 FR-006b: what an UNEDITED send looks like per language — compared at send time
		this.renderedSnapshot = { ...this.smsTemplate };
		if (this.conversationMode) {
			this.applyConversationTypeSetup(this.selectedType);
			if (!this.footerEnabled) this.smsFooter = { nl: '', fr: '', en: '', de: '' };
			this.smsMessage.smsNL = this.smsTemplate['nl'];
			this.smsMessage.smsFR = this.smsTemplate['fr'];
			this.smsMessage.smsEN = this.smsTemplate['en'];
			this.smsMessage.smsDE = this.smsTemplate['de'];
			this.updateForm();
			this.updateLang(this.data.conversation.language);
			return;
		}
		switch (this.data.smsTemplate) {
			case SmsType.LocationContactSms: {
				this.smsFooter = '';
				this.sendTo = 'send';
				this.smsMessage.role = 'location';
				const locationContactLanguage = this.data.flight.location.contact.language;
				this.languages[locationContactLanguage] += 1;
				break;
			}
			case SmsType.PilotInvitationSms: {
				this.smsFooter = {
					nl: this.translateCustom.translate('FLIGHTS.answer_pilot_yes_no', this.dutch),
					fr: this.translateCustom.translate('FLIGHTS.answer_pilot_yes_no', 'fr'),
					en: this.translateCustom.translate('FLIGHTS.answer_pilot_yes_no', 'en'),
					de: this.translateCustom.translate('FLIGHTS.answer_pilot_yes_no', 'de'),
				};
				this.positiveReply = {
					nl: this.translateCustom.translate('FLIGHTS.pilot_yes', this.dutch),
					fr: this.translateCustom.translate('FLIGHTS.pilot_yes', 'fr'),
					en: this.translateCustom.translate('FLIGHTS.pilot_yes', 'en'),
					de: this.translateCustom.translate('FLIGHTS.pilot_yes', 'de'),
				};
				this.negativeReply = {
					nl: this.translateCustom.translate('FLIGHTS.pilot_no', this.dutch),
					fr: this.translateCustom.translate('FLIGHTS.pilot_no', 'fr'),
					en: this.translateCustom.translate('FLIGHTS.pilot_no', 'en'),
					de: this.translateCustom.translate('FLIGHTS.pilot_no', 'de'),
				};
				this.sendTo = 'send';
				this.smsMessage.role = 'pilot';
				const pilotLanguage = this.data.flight.pilot.crew.language;
				this.languages[pilotLanguage] += 1;
				break;
			}
			case SmsType.CrewInvitationSms: {
				this.smsFooter = {
					nl: this.translateCustom.translate('FLIGHTS.answer_crew_yes_no', this.dutch),
					fr: this.translateCustom.translate('FLIGHTS.answer_crew_yes_no', 'fr'),
					en: this.translateCustom.translate('FLIGHTS.answer_crew_yes_no', 'en'),
					de: this.translateCustom.translate('FLIGHTS.answer_crew_yes_no', 'de'),
				};
				this.positiveReply = {
					nl: this.translateCustom.translate('BOOKINGS.crew_yes', this.dutch),
					fr: this.translateCustom.translate('BOOKINGS.crew_yes', 'fr'),
					en: this.translateCustom.translate('BOOKINGS.crew_yes', 'en'),
					de: this.translateCustom.translate('BOOKINGS.crew_yes', 'de'),
				};
				this.negativeReply = {
					nl: this.translateCustom.translate('BOOKINGS.crew_no', this.dutch),
					fr: this.translateCustom.translate('BOOKINGS.crew_no', 'fr'),
					en: this.translateCustom.translate('BOOKINGS.crew_no', 'en'),
					de: this.translateCustom.translate('BOOKINGS.crew_no', 'de'),
				};
				this.sendTo = 'send';
				this.smsMessage.role = 'crew';

				if (this.data.all) {
					this.data.flight.groundCrews.forEach((c) => {
						if (c.status === 'UNK') {
							this.languages[c.crew.language] += 1;
						}
					});
				} else {
					const lang = this.data.flight.groundCrews.find((c) => c.crew.id === this.data.crew).crew.language;
					this.languages[lang] += 1;
				}
				break;
			}
			case SmsType.PassengerInvitationSms: {
				this.smsFooter = {
					nl: this.translateCustom.translate('BOOKINGS.answer_flight_yes_no', this.dutch),
					fr: this.translateCustom.translate('BOOKINGS.answer_flight_yes_no', 'fr'),
					en: this.translateCustom.translate('BOOKINGS.answer_flight_yes_no', 'en'),
					de: this.translateCustom.translate('BOOKINGS.answer_flight_yes_no', 'de'),
				};
				this.positiveReply = {
					nl: this.translateCustom.translate('BOOKINGS.flight_yes', this.dutch),
					fr: this.translateCustom.translate('BOOKINGS.flight_yes', 'fr'),
					en: this.translateCustom.translate('BOOKINGS.flight_yes', 'en'),
					de: this.translateCustom.translate('BOOKINGS.flight_yes', 'de'),
				};
				this.negativeReply = {
					nl: this.translateCustom.translate('BOOKINGS.flight_no', this.dutch),
					fr: this.translateCustom.translate('BOOKINGS.flight_no', 'fr'),
					en: this.translateCustom.translate('BOOKINGS.flight_no', 'en'),
					de: this.translateCustom.translate('BOOKINGS.flight_no', 'de'),
				};
				// set languages
				if (this.data.all) {
					this.data.flight.bookings.list.forEach((b) => {
						if (b.status === 'ADDE') {
							this.languages[b.contactLanguage] += 1;
						}
					});
				} else {
					const lang = this.data.flight.bookings.list.find((b) => b.id === this.data.booking).contactLanguage;
					this.languages[lang] += 1;
				}
				this.sendTo = 'send_to_pax';
				this.smsMessage.role = 'PAX';
				break;
			}
			case SmsType.ReminderSms: {
				this.smsReminder = true;
				// set languages
				// if (this.data.flight.pilot) {
				// 	const pilotLanguage = this.data.flight.pilot.crew.language;
				// 	this.languages[pilotLanguage] += 1;
				// }
				// if (this.data.flight.groundCrews) {
				// 	this.data.flight.groundCrews.forEach((c) => {
				// 		if (c.status === 'CON') {
				// 			this.languages[c.crew.language] += 1;
				// 		}
				// 	});
				// }
				if (this.data.flight.bookings) {
					this.data.flight.bookings.list.forEach((b) => {
						if (b.status === 'CONF') {
							this.languages[b.contactLanguage] += 1;
						}
					});
				}

				this.sendTo = 'send_to_pax';
				this.smsMessage.role = 'PAX';
				break;
			}
			case SmsType.CancelSms: {
				this.smsCancel = true;
				// 222: recount on every (re)render — switching variant used to double the counts
				this.languages = { nl: 0, fr: 0, en: 0, de: 0 };
				// set languages — include confirmed crew & pilot so the cancellation
				// can also be sent to them (031-crew-cancel-notification)
				if (this.data.flight.pilot) {
					const pilotLanguage = this.data.flight.pilot.crew.language;
					this.languages[pilotLanguage] += 1;
				}
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'CON') {
						this.languages[c.crew.language] += 1;
					}
				});
				this.reminderCancelRecipients.forEach((b) => {
					this.languages[b.contactLanguage] += 1;
				});
				this.sendTo = 'send_to_pax';
				this.smsMessage.role = 'PAX';
				break;
			}
		}

		// reset footer if not enabled
		if (!this.footerEnabled) {
			this.smsFooter = {
				nl: '',
				fr: '',
				en: '',
				de: '',
			};
		}

		this.smsMessage.smsNL = this.smsTemplate['nl'];
		this.smsMessage.smsFR = this.smsTemplate['fr'];
		this.smsMessage.smsEN = this.smsTemplate['en'];
		this.smsMessage.smsDE = this.smsTemplate['de'];
		this.updateForm();
		// make sure language selected is available
		if (this.languages.nl > 0) {
			this.updateLang('nl');
			return;
		} else if (this.languages.fr > 0) {
			this.updateLang('fr');
			return;
		} else if (this.languages.en > 0) {
			this.updateLang('en');
			return;
		} else if (this.languages.de > 0) {
			this.updateLang('de');
			return;
		}
	}

	getSMSSelected(lang: string) {
		switch (lang) {
			case 'nl':
				return this.waDetailForm.controls['smsNL'].value;
			case 'fr':
				return this.waDetailForm.controls['smsFR'].value;
			case 'en':
				return this.waDetailForm.controls['smsEN'].value;
			case 'de':
				return this.waDetailForm.controls['smsDE'].value;
		}
	}

	parseTemplate(smsTemplate: string, flight, lang: string) {
		let customLang = lang;
		if (this.translate.currentLang === 'du' && customLang === 'nl') customLang = 'du';
		const reBalloon = /{{ balloon }}/gi;
		const reHour = /{{ hour }}/gi;
		const reHourMeeting = /{{ meeting_hour }}/gi;
		const reCity = /{{ city }}/gi;
		const rePeriod = /{{ period }}/gi;
		const reDate = /{{ date }}/gi;
		const reAddress = /{{ address }}/gi;
		const reLocation = /{{ location }}/gi;
		const reCountry = /{{ country }}/gi;

		let smsText = smsTemplate;
		// try {
		// 	smsText = JSON.parse(smsTemplate)[lang];
		// } catch (e) {}

		// 2026-09-03 (FR-030): values the context cannot fill render as the visible '-' — the same
		// substitute the backend send path delivers (buildTemplateParams), so preview == sent. This
		// also covers conversation mode, where the flight may be absent or partial.
		smsText = smsText.replace(reBalloon, flight?.balloon?.name ?? '-');
		smsText = smsText.replace(reHour, flight?.hour ? this.formatTime(flight.hour) : '-');
		// meeting hour = hourMeeting (the backend renders the same field); older flights without one fall back to the flight hour
		const meetingHour = flight?.hourMeeting ?? flight?.hour;
		smsText = smsText.replace(reHourMeeting, meetingHour ? this.formatTime(meetingHour) : '-');
		smsText = smsText.replace(reCity, flight?.location?.city ?? '-');
		smsText = smsText.replace(rePeriod, flight?.period ? this.translateCustom.translate('FLIGHTS.' + flight.period, customLang) : '-');
		smsText = smsText.replace(reDate, flight?.date ? this.formatDate(flight.date, lang) : '-');
		smsText = smsText.replace(reAddress, flight?.location?.address ?? '-');
		smsText = smsText.replace(reLocation, flight?.location?.description ?? '-');
		smsText = smsText.replace(reCountry, flight?.location?.country ?? '-');
		// check-in vocabulary (registry CHECKIN_PLACEHOLDERS, review 2026-09-13) — the same names and formats
		// the backend renders (buildTemplateParams / check-in e-mail), so preview == delivered
		const checkinValues: Record<string, string> = {
			flight_date: flight?.date ? dayjs(flight.date).locale(lang).format('dddd DD MMMM YYYY') : '',
			flight_hour: flight?.hour ? this.formatTime(flight.hour) : '',
			flight_meeting_hour: flight?.hourMeeting ? this.formatTime(flight.hourMeeting) : '',
			location_name: flight?.location?.name || '',
			location_city: flight?.location?.city || '',
		};
		for (const [key, value] of Object.entries(checkinValues)) {
			smsText = smsText.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'gi'), value.trim() || '-');
		}
		// 2026-09-12: booking placeholders of the reservation confirmation — the SAME booking_* names
		// (and values) the confirmation e-mail renders (backend buildBookingPlaceholders); conversation
		// mode passes data.booking, the contact name also lives on the conversation itself. Unknown
		// values become the backend's visible '-' (buildTemplateParams) so preview == delivered.
		// this.data.booking is a full Booking object in conversation mode, but only a booking ID
		// string when opened from the flight-detail single-passenger invite flow (see the lookup
		// at line ~492 above) — resolve it against the flight's booking list in that case.
		const booking =
			typeof this.data.booking === 'string' ? this.data.flight?.bookings?.list?.find((b) => b.id === this.data.booking) : this.data.booking;
		const pc = booking?.passengerCounters || {};
		const num = (value: any) => (value != null ? String(value) : '');
		let availabilityDate = booking?.availabilityType ? this.translateCustom.translate('BOOKINGS.' + booking.availabilityType, customLang) : '';
		if (booking?.availabilityType === 'DATE' && booking?.preferredFlights?.length > 0) {
			availabilityDate = dayjs(booking.preferredFlights[0].date).locale(lang).format('dddd DD MMMM YYYY');
		}
		const bookingValues: Record<string, string> = {
			booking_name: booking?.contactName || this.data.conversation?.name || '',
			booking_code: booking?.bookingCode || '',
			booking_passengers: num(pc.passengerCount),
			booking_adults: pc.passengerCount != null ? String(pc.passengerCount - (pc.childCount || 0)) : '',
			booking_childs: num(pc.childCount),
			booking_special: num(pc.disabilityCount),
			booking_weight: num(pc.totalWeight),
			booking_region: booking?.region?.name || '',
			booking_availability_date: availabilityDate,
			booking_total_amount: num(pc.totalAmount),
			booking_open_amount: num(pc.openAmount),
			booking_comment: booking?.comments || '',
		};
		for (const [key, value] of Object.entries(bookingValues)) {
			smsText = smsText.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'gi'), value.trim() || '-');
		}
		// any placeholder this dialog cannot fill (e.g. the check-in type's {{ booking_name }})
		// is delivered as '-' by the send path (buildTemplateParams) — mirror that here so the
		// operator never sees raw {{ }} that would silently become something else
		smsText = smsText.replace(/{{\s*\w+\s*}}/g, '-');
		return smsText;
	}

	formatDate(date, lang) {
		const localMoment = dayjs(date);
		return localMoment.locale(lang).format('dd D MMM');
	}

	formatToday() {
		return dayjs().format('dd D/MM/YY');
	}

	formatTime(time: string) {
		return dayjs(time).format('HH:mm');
	}

	updateForm() {
		this.waDetailForm.patchValue(this.smsMessage);
	}

	// 037 FR-029: swap to another variant's wording (re-renders all four languages)
	selectVariant(variant) {
		this.settings = variant;
		// 222: a coded variant carries the cancel reason; the general one keeps the chosen reason
		if (this.cancelFlightMode && !this.data.cancelFlight.alreadySent && variant.reasonCode) {
			this.cancelReason = variant.reasonCode;
			this.reasonEditing = false;
		}
		this.updateTemplate();
	}

	onNoClick(): void {
		this.dialogRef.close('cancel');
	}

	updateLang(lang) {
		this.langSelected = lang;
		this.chipListSelected = this.chipLists[lang];
	}

	getNumberOfLanguages() {
		let count = 0;
		if (this.languages.nl > 0) {
			count += 1;
		}
		if (this.languages.fr > 0) {
			count += 1;
		}
		if (this.languages.en > 0) {
			count += 1;
		}
		if (this.languages.de > 0) {
			count += 1;
		}
		return count;
	}

	chipClick(smsText: string, lang: string) {
		switch (lang) {
			case 'nl':
				this.smsMessage.smsNL = this.waDetailForm.controls['smsNL'].value.trim() + ' ' + smsText;
				break;
			case 'fr':
				this.smsMessage.smsFR = this.waDetailForm.controls['smsFR'].value.trim() + ' ' + smsText;
				break;
			case 'en':
				this.smsMessage.smsEN = this.waDetailForm.controls['smsEN'].value.trim() + ' ' + smsText;
				break;
			case 'de':
				this.smsMessage.smsDE = this.waDetailForm.controls['smsDE'].value.trim() + ' ' + smsText;
				break;
		}
		this.updateForm();
	}

	onImageSelectionChanged(images: ImageItem[]) {
		this.selectedImages = images;
	}

	cancel() {
		// if (this.smsCancel) {
		// 	this.flightService.updateFlightStatus(this.data.flight).subscribe();
		// }
		this.dialogRef.close('cancel');
	}

	// True when the flight has anyone to notify on cancellation: a pilot or at
	// least one confirmed ground crew member (031-crew-cancel-notification).
	get hasCrew(): boolean {
		const hasPilot = !!this.data.flight.pilot;
		const hasConfirmedCrew = (this.data.flight.groundCrews || []).some((c) => c.status === 'CON');
		return hasPilot || hasConfirmedCrew;
	}

	// Send to passengers only (conversation mode: the single chat recipient).
	confirm(smsMessage: any) {
		if (this.conversationMode) {
			this.sendConversation(smsMessage);
			return;
		}
		this.sendSMS(smsMessage, false, true);
		this.dialogRef.close('confirm');
	}

	// Send to passengers and crew & pilots.
	confirmToAll(smsMessage: any) {
		this.sendSMS(smsMessage, true, true);
		this.dialogRef.close('confirm');
	}

	// 222: fire a reminder/cancel send, or collect it when the caller must await all of them
	private dispatchSend(send: Observable<any>) {
		if (this.sendCollector) this.sendCollector.push(send);
		else send.subscribe();
	}

	/** 222: the reason row shows the variant's reason read-only unless the operator overrides it */
	get reasonFromVariant(): boolean {
		// message already sent: the reason comes from the variant that went out
		const fromMessage = this.data.cancelFlight?.alreadySent ? this.data.cancelFlight.cancelMessageReason : this.settings?.reasonCode;
		return !!fromMessage && !this.reasonEditing && this.cancelReason === fromMessage;
	}

	// 222: primary action — queue the cancel messages, and only when ALL were accepted hand the
	// cancellation back to the flight; a refused send keeps the dialog open and nothing is cancelled
	cancelAndSend(withCrew: boolean) {
		if (this.sending) return;
		this.sending = true;
		this.sendError = null;
		this.sendCollector = [];
		this.sendSMS(this.waDetailForm.value, withCrew, true);
		const sends = this.sendCollector;
		this.sendCollector = null;
		const done = () => this.dialogRef.close({ action: 'cancelAndSend', cancelReason: this.cancelReason });
		if (!sends.length) return done();
		forkJoin(sends).subscribe({
			next: done,
			error: (err) => {
				this.sending = false;
				this.sendError = err?.message || String(err);
			},
		});
	}

	// 222: secondary action — cancel without telling the passengers (the flight records that)
	cancelOnly() {
		this.dialogRef.close({ action: 'cancelOnly', cancelReason: this.cancelReason });
	}

	// 222: the cancel message already went out (message-first path) — just cancel
	cancelAlreadySent() {
		this.dialogRef.close({ action: 'cancel', cancelReason: this.cancelReason ?? this.data.cancelFlight?.cancelMessageReason ?? null });
	}

	// 037 FR-006c: recipient phones for the current message type — informational window count
	private recipientPhones(): string[] {
		if (this.conversationMode) return [this.data.conversation.phone].filter(Boolean);
		const flight = this.data?.flight || {};
		const phones: string[] = [];
		switch (this.data?.smsTemplate) {
			case SmsType.PilotInvitationSms:
				phones.push(flight.pilot?.crew?.phone);
				break;
			case SmsType.CrewInvitationSms:
				(flight.groundCrews || []).forEach((c: any) => phones.push(c?.crew?.phone));
				break;
			case SmsType.LocationContactSms:
				phones.push(flight.location?.contact?.phone);
				break;
			default: {
				const bookings = this.data?.all
					? [...(flight.bookings?.list || []), ...(this.data?.formerBookings || [])]
					: (flight.bookings?.list || []).filter((b: any) => b.id === this.data?.booking);
				bookings.forEach((b: any) => phones.push(b?.contactPhone));
			}
		}
		return [...new Set(phones.filter(Boolean))];
	}

	private checkRecipientWindows() {
		const phones = this.recipientPhones();
		if (!phones.length) return;
		this.whatsappWindowStatesGQL.fetch({ phones }).subscribe({
			next: (result) => {
				const states = result.data?.whatsappWindowStates || [];
				this.recipientCount = states.length;
				this.outOfWindowCount = states.filter((state) => !state.open).length;
				this.windowChecked = true;
				// a toggle switched on before the window states arrived must not ride along; the
				// controls are disabled on the form itself (not via [disabled], which reactive
				// forms warn about) so the send payload cannot carry them either
			},
			error: () => {},
		});
	}

	/** live edit detection against the rendered snapshot — drives the amber warning */
	get textEdited(): boolean {
		const value = this.waDetailForm?.value || {};
		return ['nl', 'fr', 'en', 'de'].some((lang) => {
			const snapshot = this.renderedSnapshot?.[lang];
			return snapshot !== undefined && (value['sms' + lang.toUpperCase()] ?? '') !== snapshot;
		});
	}

	// 037 FR-007/FR-029: template choice + edited flag carried on every send. Edited is judged
	// across all languages (conservative: any edit ⇒ out-of-window recipients fall back to SMS
	// rather than receiving a template wording the operator did not see, FR-006b).
	private templateMeta(smsMessage: any) {
		const edited = ['nl', 'fr', 'en', 'de'].some(
			(lang) => (this.renderedSnapshot?.[lang] ?? '') !== (smsMessage?.['sms' + lang.toUpperCase()] ?? ''),
		);
		return {
			smsType: (this.conversationMode ? this.selectedType : this.data.smsTemplate) || null,
			smsTemplateId: this.settings?.id || null,
			templateEdited: edited,
			flightId: this.data.flight?.id || null,
		};
	}

	// 037 US7 (2026-09-03): conversation mode sends to exactly one recipient over the typed
	// path (sendBookingSms/sendCrewSms — SmsInput carries the template meta), then reports
	// 'sent' so the chat marks the thread read. The dialog only closes on success.
	private sendConversation(smsMessage: any) {
		if (this.sending || !this.selectedType) return;
		this.sending = true;
		const lang = this.data.conversation.language;
		const suffix = { nl: 'NL', fr: 'FR', en: 'EN', de: 'DE' }[lang] || 'EN';
		let smsText = smsMessage['sms' + suffix] || '';
		if (this.smsFooter?.[lang]) smsText = `${smsText} ${this.smsFooter[lang]}`;
		let send;
		if (this.data.conversation.type === 'BOO' && this.selectedType === SmsType.BookingConfirmationSms) {
			// reservation confirmation may carry the UP url and upcoming flight options — those ride
			// as separate messages, which only the sendBookingMessage path assembles
			const messageInput: MessageInput = {
				...(this.templateMeta(smsMessage) as any),
				text: smsText,
				up: !!smsMessage.up,
				flightOptions: !!smsMessage.flightOptions,
				repeat: SmsRepeat.Single,
			};
			send = this.smsService.sendBookingMessage(this.data.conversation.id, messageInput);
		} else {
			const messageInput: MessageInput = {
				...(this.templateMeta(smsMessage) as any),
				text: smsText,
				up: null,
				positiveReply: this.positiveReply?.[lang] ?? '',
				negativeReply: this.negativeReply?.[lang] ?? '',
				repeat: SmsRepeat.Single,
			};
			send =
				this.data.conversation.type === 'BOO'
					? this.smsService.sendBookingSMS(this.data.conversation.id, messageInput)
					: this.smsService.sendCrewSms(this.data.conversation.id, messageInput);
		}
		// fire-and-forget like the chat's own composer: close immediately (Wim, 2026-09-04) —
		// the thread's status icon reports delivery/failure (US8), the dialog need not wait
		send.subscribe({
			error: (error) => console.error('[dialog-wa] conversation template send failed:', error),
		});
		this.dialogRef.close('sent');
	}

	sendSMS(smsMessage: any, sendToCrew: boolean, sendToPax: boolean = true) {
		let smsText = '';
		let positiveReply = '';
		let negativeReply = '';
		// invite PILOT
		if (smsMessage.role === 'pilot') {
			const pilotLanguage = this.data.flight.pilot.crew.language;
			switch (pilotLanguage) {
				case 'nl':
					smsText = smsMessage.smsNL;
					positiveReply = this.positiveReply.nl;
					negativeReply = this.negativeReply.nl;
					break;
				case 'fr':
					smsText = smsMessage.smsFR;
					positiveReply = this.positiveReply.fr;
					negativeReply = this.negativeReply.fr;
					break;
				case 'en':
					smsText = smsMessage.smsEN;
					positiveReply = this.positiveReply.en;
					negativeReply = this.negativeReply.en;
					break;
				case 'de':
					smsText = smsMessage.smsDE;
					positiveReply = this.positiveReply.de;
					negativeReply = this.negativeReply.de;
					break;
			}
			const messageInput: MessageInput = {
				...(this.templateMeta(smsMessage) as any),
				text: smsText,
				up: null,
				positiveReply,
				negativeReply,
				repeat: SmsRepeat.Single,
			};
			this.smsService.sendCrewSms(this.data.flight.pilot.crew.id, messageInput).subscribe();
		}
		// invite 1 CREW
		if (smsMessage.role === 'crew' && !this.data.all) {
			if (this.languages.nl > 0) {
				smsText = smsMessage.smsNL;
				positiveReply = this.positiveReply.nl;
				negativeReply = this.negativeReply.nl;
			}
			if (this.languages.fr > 0) {
				smsText = smsMessage.smsFR;
				positiveReply = this.positiveReply.fr;
				negativeReply = this.negativeReply.fr;
			}
			if (this.languages.en > 0) {
				smsText = smsMessage.smsEN;
				positiveReply = this.positiveReply.en;
				negativeReply = this.negativeReply.en;
			}
			if (this.languages.de > 0) {
				smsText = smsMessage.smsDE;
				positiveReply = this.positiveReply.de;
				negativeReply = this.negativeReply.de;
			}
			const messageInput: MessageInput = {
				...(this.templateMeta(smsMessage) as any),
				text: smsText,
				up: null,
				positiveReply,
				negativeReply,
				repeat: SmsRepeat.Single,
			};
			this.smsService.sendCrewSms(this.data.crew, messageInput).subscribe();
		}
		// invite ALL CREW
		if (smsMessage.role === 'crew' && this.data.all) {
			if (this.languages.nl > 0) {
				smsText = smsMessage.smsNL;
				positiveReply = this.positiveReply.nl;
				negativeReply = this.negativeReply.nl;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'UNK' && c.crew.language === 'nl') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
						};
						this.smsService.sendCrewSms(c.crew.id, messageInput).subscribe();
					}
				});
			}
			if (this.languages.fr > 0) {
				smsText = smsMessage.smsFR;
				positiveReply = this.positiveReply.fr;
				negativeReply = this.negativeReply.fr;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'UNK' && c.crew.language === 'fr') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
						};
						this.smsService.sendCrewSms(c.crew.id, messageInput).subscribe();
					}
				});
			}
			if (this.languages.en > 0) {
				smsText = smsMessage.smsEN;
				positiveReply = this.positiveReply.en;
				negativeReply = this.negativeReply.en;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'UNK' && c.crew.language === 'en') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
						};
						this.smsService.sendCrewSms(c.crew.id, messageInput).subscribe();
					}
				});
			}
			if (this.languages.de > 0) {
				smsText = smsMessage.smsDE;
				positiveReply = this.positiveReply.de;
				negativeReply = this.negativeReply.de;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'UNK' && c.crew.language === 'de') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
						};
						this.smsService.sendCrewSms(c.crew.id, messageInput).subscribe();
					}
				});
			}
		}
		// Notify location contact
		if (smsMessage.role === 'location') {
			const lang = this.data.flight.location.contact.language;
			switch (lang) {
				case 'nl':
					smsText = `${smsMessage.smsNL}`;
					break;
				case 'fr':
					smsText = `${smsMessage.smsFR}`;
					break;
				case 'en':
					smsText = `${smsMessage.smsEN}`;
					break;
				case 'de':
					smsText = `${smsMessage.smsDE}`;
					break;
			}
			const messageInput: MessageInput = {
				...(this.templateMeta(smsMessage) as any),
				text: smsText,
				up: false,
				positiveReply,
				negativeReply,
				repeat: SmsRepeat.Single,
			};
			this.smsService.sendLocationSMS(this.data.flight.location.id, this.data.flight.id, messageInput).subscribe();
		}
		// invite 1 PAX
		if (smsMessage.role === 'PAX' && !this.data.all) {
			const lang = this.data.flight.bookings.list.find((b) => b.id === this.data.booking).contactLanguage;
			switch (lang) {
				case 'nl':
					smsText = `${smsMessage.smsNL} ${this.smsFooter.nl}`;
					positiveReply = this.positiveReply.nl;
					negativeReply = this.negativeReply.nl;
					break;
				case 'fr':
					smsText = `${smsMessage.smsFR} ${this.smsFooter.fr}`;
					positiveReply = this.positiveReply.fr;
					negativeReply = this.negativeReply.fr;
					break;
				case 'en':
					smsText = `${smsMessage.smsEN} ${this.smsFooter.en}`;
					positiveReply = this.positiveReply.en;
					negativeReply = this.negativeReply.en;
					break;
				case 'de':
					smsText = `${smsMessage.smsDE} ${this.smsFooter.de}`;
					positiveReply = this.positiveReply.de;
					negativeReply = this.negativeReply.de;
					break;
			}
			const messageInput: MessageInput = {
				...(this.templateMeta(smsMessage) as any),
				text: smsText,
				up: smsMessage.up,
				flightOptions: smsMessage.flightOptions,
				positiveReply,
				negativeReply,
				repeat: SmsRepeat.Single,
				images: [],
			};
			this.smsService.sendBookingMessage(this.data.booking, messageInput).subscribe();
		}
		// invite ALL PAX
		if (smsMessage.role === 'PAX' && this.data.all) {
			if (!this.smsReminder && !this.smsCancel) {
				if (this.languages.nl > 0) {
					smsText = `${smsMessage.smsNL} ${this.smsFooter.nl}`;
					positiveReply = this.positiveReply.nl;
					negativeReply = this.negativeReply.nl;
					this.data.flight.bookings.list.forEach((b) => {
						if (b.status === 'ADDE' && b.contactLanguage === 'nl') {
							const messageInput: MessageInput = {
								text: smsText,
								up: smsMessage.up,
								flightOptions: smsMessage.flightOptions,
								positiveReply,
								negativeReply,
								repeat: SmsRepeat.Single,
								images: this.images,
							};
							this.smsService.sendBookingMessage(b.id, messageInput).subscribe();
						}
					});
				}
				if (this.languages.fr > 0) {
					smsText = `${smsMessage.smsFR} ${this.smsFooter.fr}`;
					positiveReply = this.positiveReply.fr;
					negativeReply = this.negativeReply.fr;
					this.data.flight.bookings.list.forEach((b) => {
						if (b.status === 'ADDE' && b.contactLanguage === 'fr') {
							const messageInput: MessageInput = {
								text: smsText,
								up: smsMessage.up,
								flightOptions: smsMessage.flightOptions,
								positiveReply,
								negativeReply,
								repeat: SmsRepeat.Single,
								images: this.images,
							};
							this.smsService.sendBookingMessage(b.id, messageInput).subscribe();
						}
					});
				}
				if (this.languages.en > 0) {
					smsText = `${smsMessage.smsEN} ${this.smsFooter.en}`;
					positiveReply = this.positiveReply.en;
					negativeReply = this.negativeReply.en;
					this.data.flight.bookings.list.forEach((b) => {
						if (b.status === 'ADDE' && b.contactLanguage === 'en') {
							const messageInput: MessageInput = {
								text: smsText,
								up: smsMessage.up,
								flightOptions: smsMessage.flightOptions,
								positiveReply,
								negativeReply,
								repeat: SmsRepeat.Single,
								images: this.images,
							};
							this.smsService.sendBookingMessage(b.id, messageInput).subscribe();
						}
					});
				}
				if (this.languages.de > 0) {
					smsText = `${smsMessage.smsDE} ${this.smsFooter.de}`;
					positiveReply = this.positiveReply.de;
					negativeReply = this.negativeReply.de;
					this.data.flight.bookings.list.forEach((b) => {
						if (b.status === 'ADDE' && b.contactLanguage === 'de') {
							const messageInput: MessageInput = {
								text: smsText,
								up: smsMessage.up,
								flightOptions: smsMessage.flightOptions,
								positiveReply,
								negativeReply,
								repeat: SmsRepeat.Single,
								images: this.images,
							};
							this.smsService.sendBookingMessage(b.id, messageInput).subscribe();
						}
					});
				}
			}
		}
		// remind or cancel ALL PAX
		if ((this.smsReminder || this.smsCancel) && sendToPax) {
			if (this.languages.nl > 0) {
				smsText = smsMessage.smsNL;
				this.reminderCancelRecipients.forEach((b) => {
					if (b.contactLanguage === 'nl') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: smsMessage.up,
							flightOptions: smsMessage.flightOptions,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendBookingMessage(b.id, messageInput));
					}
				});
			}
			if (this.languages.fr > 0) {
				smsText = smsMessage.smsFR;
				this.reminderCancelRecipients.forEach((b) => {
					if (b.contactLanguage === 'fr') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: smsMessage.up,
							flightOptions: smsMessage.flightOptions,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendBookingMessage(b.id, messageInput));
					}
				});
			}
			if (this.languages.en > 0) {
				smsText = smsMessage.smsEN;
				this.reminderCancelRecipients.forEach((b) => {
					if (b.contactLanguage === 'en') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: smsMessage.up,
							flightOptions: smsMessage.flightOptions,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendBookingMessage(b.id, messageInput));
					}
				});
			}
			if (this.languages.de > 0) {
				smsText = smsMessage.smsDE;
				this.reminderCancelRecipients.forEach((b) => {
					if (b.contactLanguage === 'de') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: smsMessage.up,
							flightOptions: smsMessage.flightOptions,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendBookingMessage(b.id, messageInput));
					}
				});
			}
		}
		// remind or cancel ALL CREW & pilot
		if ((this.smsReminder || this.smsCancel) && sendToCrew) {
			if (this.languages.nl > 0) {
				smsText = smsMessage.smsNL;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'CON' && c.crew.language === 'nl') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(c.crew.id, messageInput));
					}
				});
				if (this.data.flight.pilot) {
					if (this.data.flight.pilot.crew.language === 'nl') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(this.data.flight.pilot.crew.id, messageInput));
					}
				}
			}
			if (this.languages.fr > 0) {
				smsText = smsMessage.smsFR;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'CON' && c.crew.language === 'fr') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(c.crew.id, messageInput));
					}
				});
				if (this.data.flight.pilot) {
					if (this.data.flight.pilot.crew.language === 'fr') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(this.data.flight.pilot.crew.id, messageInput));
					}
				}
			}
			if (this.languages.en > 0) {
				smsText = smsMessage.smsEN;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'CON' && c.crew.language === 'en') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(c.crew.id, messageInput));
					}
				});
				if (this.data.flight.pilot) {
					if (this.data.flight.pilot.crew.language === 'en') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(this.data.flight.pilot.crew.id, messageInput));
					}
				}
			}
			if (this.languages.de > 0) {
				smsText = smsMessage.smsDE;
				this.data.flight.groundCrews.forEach((c) => {
					if (c.status === 'CON' && c.crew.language === 'de') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(c.crew.id, messageInput));
					}
				});
				if (this.data.flight.pilot) {
					if (this.data.flight.pilot.crew.language === 'de') {
						const messageInput: MessageInput = {
							...(this.templateMeta(smsMessage) as any),
							text: smsText,
							up: null,
							positiveReply,
							negativeReply,
							repeat: SmsRepeat.Single,
							images: this.images,
						};
						this.dispatchSend(this.smsService.sendCrewMessage(this.data.flight.pilot.crew.id, messageInput));
					}
				}
			}
		}
		if (this.smsCancel) {
			// this.flightService.updateFlightStatus(this.data.flight).subscribe();
		}
	}
}
